/**
 * MAIL-08: dedicated Email/channel admin UI.
 *
 * Shows all EmailConfig records for the account with full field editing
 * (name, email address, reply-to, SMTP settings, IMAP settings, default/active
 * toggles). The generic SettingsPages 'email' entry only exposed name/host/port
 * — this replaces it with a proper editor that covers every EmailConfig column.
 */
import * as React from 'react';
import { api } from '@/lib/api-client';
import { useI18n } from '@/lib/i18n';
import {
  Button, Card, CardContent, CardHeader, CardTitle, EmptyState, ErrorState, Input, PageHeader, SkeletonRow, useConfirm,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui';

interface EmailConfig {
  id: string;
  name: string;
  email: string;
  replyTo: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUser: string | null;
  smtpPassword: string | null;
  imapHost: string | null;
  imapPort: number | null;
  imapUser: string | null;
  imapPassword: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
}

type FormState = {
  name: string;
  email: string;
  replyTo: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  isDefault: boolean;
  isActive: boolean;
};

const EMPTY_FORM: FormState = {
  name: '', email: '', replyTo: '', smtpHost: '', smtpPort: '', smtpUser: '',
  smtpPassword: '', isDefault: false, isActive: true,
};

function toForm(config: EmailConfig): FormState {
  return {
    name: config.name,
    email: config.email,
    replyTo: config.replyTo ?? '',
    smtpHost: config.smtpHost ?? '',
    smtpPort: config.smtpPort?.toString() ?? '',
    smtpUser: config.smtpUser ?? '',
    smtpPassword: '',
    isDefault: config.isDefault,
    isActive: config.isActive,
  };
}

function toPayload(form: FormState) {
  return {
    name: form.name,
    email: form.email,
    replyTo: form.replyTo || undefined,
    smtpHost: form.smtpHost || undefined,
    smtpPort: form.smtpPort ? Number(form.smtpPort) : undefined,
    smtpUser: form.smtpUser || undefined,
    smtpPassword: form.smtpPassword || undefined,
    isDefault: form.isDefault,
    isActive: form.isActive,
  };
}

export function EmailConfigsPage() {
  const { t } = useI18n();
  const { confirm } = useConfirm();
  const [configs, setConfigs] = React.useState<EmailConfig[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [editing, setEditing] = React.useState<string | 'new' | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get<EmailConfig[]>('/admin/email-configs');
      setConfigs(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('adm.email.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  function startCreate() { setEditing('new'); setForm(EMPTY_FORM); }
  function startEdit(config: EmailConfig) { setEditing(config.id); setForm(toForm(config)); }
  function cancelEdit() { setEditing(null); setForm(EMPTY_FORM); }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editing === 'new') {
        await api.post('/admin/email-configs', toPayload(form));
      } else if (editing) {
        await api.put(`/admin/email-configs/${editing}`, toPayload(form));
      }
      cancelEdit();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('adm.email.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!await confirm({ title: t('adm.email.confirmTitle'), description: t('adm.email.confirmDesc'), confirmLabel: t('adm.email.confirmLabel') })) return;
    try { await api.delete(`/admin/email-configs/${id}`); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : t('adm.email.deleteFailed')); }
  }

  const setField = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  return (
    <div className="space-y-6">
      <PageHeader
        className="mb-0"
        breadcrumb={[{ label: t('nav.settings'), to: '/settings' }, { label: t('adm.email.title') }]}
        title={t('adm.email.title')}
        description={t('adm.email.description')}
        actions={(
          <>
            <Button variant="outline" onClick={() => void load()}>{t('adm.refresh')}</Button>
            <Button onClick={startCreate}>{t('adm.email.addChannel')}</Button>
          </>
        )}
      />

      {error && <ErrorState message={error} onRetry={() => void load()} />}

      {editing !== null && (
        <Card>
          <CardHeader><CardTitle>{editing === 'new' ? t('adm.email.addTitle') : t('adm.email.editTitle')}</CardTitle></CardHeader>
          <CardContent>
            <form className="grid gap-4 md:grid-cols-2" onSubmit={save}>
              <Input label={t('adm.nameRequired')} value={form.name} onChange={setField('name')} required />
              <Input label={t('adm.email.emailAddress')} type="email" value={form.email} onChange={setField('email')} required />
              <Input label={t('adm.email.replyTo')} type="email" value={form.replyTo} onChange={setField('replyTo')} placeholder={t('adm.email.replyToPh')} />
              <div className="md:col-span-2 border-t pt-4 mt-2"><h3 className="text-sm font-semibold text-ink-muted mb-3">{t('adm.email.outbound')}</h3></div>
              <Input label={t('adm.email.smtpHost')} value={form.smtpHost} onChange={setField('smtpHost')} placeholder={t('adm.email.smtpHostPh')} />
              <Input label={t('adm.email.smtpPort')} type="number" value={form.smtpPort} onChange={setField('smtpPort')} placeholder="587" />
              <Input label={t('adm.email.smtpUser')} value={form.smtpUser} onChange={setField('smtpUser')} />
              <Input label={t('adm.email.smtpPassword')} type="password" value={form.smtpPassword} onChange={setField('smtpPassword')} placeholder={editing !== 'new' ? t('adm.jira.unchanged') : ''} />
              <div className="md:col-span-2 border-t pt-4 mt-2 flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={setField('isActive')} className="h-4 w-4 rounded border-line-strong" /> {t('adm.active')}</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isDefault} onChange={setField('isDefault')} className="h-4 w-4 rounded border-line-strong" /> {t('adm.email.defaultChannel')}</label>
              </div>
              <div className="md:col-span-2 flex gap-2 pt-2">
                <Button type="submit" disabled={saving}>{saving ? t('common.saving') : editing === 'new' ? t('adm.email.create') : t('adm.saveChanges')}</Button>
                <Button type="button" variant="outline" onClick={cancelEdit}>{t('common.cancel')}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {loading ? <Card><CardContent className="p-0"><SkeletonRow rows={3} /></CardContent></Card> : (
        <Card>
          <CardHeader><CardTitle>{t(configs.length === 1 ? 'adm.email.countOne' : 'adm.email.countMany', { count: configs.length })}</CardTitle></CardHeader>
          <CardContent className="p-0">
            {configs.length === 0 ? <EmptyState title={t('adm.email.createFirst')} description={t('adm.email.createFirstDesc')} /> : (
              <Table>
                <TableHeader><TableRow><TableHead>{t('common.name')}</TableHead><TableHead>{t('adm.email.colEmail')}</TableHead><TableHead>{t('adm.email.colReplyTo')}</TableHead><TableHead>{t('adm.email.colSmtp')}</TableHead><TableHead>{t('adm.status')}</TableHead><TableHead>{t('common.actions')}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {configs.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.name}{c.isDefault && <span className="ml-2 rounded bg-brand-100 px-1.5 py-0.5 text-xs text-brand-700">{t('adm.email.default')}</span>}</TableCell>
                      <TableCell>{c.email}</TableCell>
                      <TableCell>{c.replyTo || '\u2014'}</TableCell>
                      <TableCell className="text-xs text-ink-subtle">{c.smtpHost ? `${c.smtpHost}:${c.smtpPort ?? '?'}` : <span className="text-warning-700">{t('adm.email.sesGlobal')}</span>}</TableCell>
                      <TableCell>{c.isActive ? <span className="text-success-700 text-xs font-medium">{t('adm.active')}</span> : <span className="text-ink-subtle text-xs">{t('adm.inactive')}</span>}</TableCell>
                      <TableCell><div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => startEdit(c)}>{t('adm.edit')}</Button><Button size="sm" variant="destructive" onClick={() => void remove(c.id)}>{t('common.delete')}</Button></div></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
